import Api from "@/config/Api";

const HttpClient = Api();

export default {
  state: {
    billingPeriod: "monthly",
    selectedPlanSlug: null,
    firma: false,
    marketingAddon: false,
    couponCode: "",
    couponValid: null,
    plans: [],
    plansLoaded: false,
    userPlan: null,
    userPlanLoaded: false,
    paymentMethod: "card",
    bankAccounts: [],
    bankAccountsLoaded: false,
    limitModal: {
      visible: false,
      feature: null,
      usage: null,
      warning: false
    }
  },
  mutations: {
    SET_PAYMENT_METHOD(state, method) {
      state.paymentMethod = method;
    },
    SET_BANK_ACCOUNTS(state, accounts) {
      state.bankAccounts = accounts;
      state.bankAccountsLoaded = true;
    },
    SET_BILLING_PERIOD(state, period) {
      state.billingPeriod = period;
    },
    SET_SELECTED_PLAN(state, slug) {
      state.selectedPlanSlug = slug;
    },
    SET_FIRMA(state, val) {
      state.firma = val;
    },
    SET_MARKETING_ADDON(state, val) {
      state.marketingAddon = val;
    },
    SET_COUPON_CODE(state, code) {
      state.couponCode = code;
    },
    SET_COUPON_VALID(state, result) {
      state.couponValid = result;
    },
    CLEAR_COUPON(state) {
      state.couponCode = "";
      state.couponValid = null;
    },
    SET_PLANS(state, plans) {
      state.plans = plans;
      state.plansLoaded = true;
    },
    SET_USER_PLAN(state, data) {
      state.userPlan = data;
      state.userPlanLoaded = true;
    },
    RESET_PLANS_LOADED(state) {
      state.plansLoaded = false;
    },
    SHOW_LIMIT_MODAL(state, { feature, usage, warning }) {
      state.limitModal = { visible: true, feature, usage, warning: !!warning };
    },
    HIDE_LIMIT_MODAL(state) {
      state.limitModal = { ...state.limitModal, visible: false };
    }
  },
  actions: {
    setBillingPeriod({ commit }, period) {
      commit("SET_BILLING_PERIOD", period);
    },
    setSelectedPlan({ commit }, slug) {
      commit("SET_SELECTED_PLAN", slug);
    },
    setFirma({ commit }, val) {
      commit("SET_FIRMA", val);
    },
    setMarketingAddon({ commit }, val) {
      commit("SET_MARKETING_ADDON", val);
    },
    setPaymentMethod({ commit }, method) {
      commit("SET_PAYMENT_METHOD", method);
    },
    async fetchBankAccounts({ commit, state }) {
      if (state.bankAccountsLoaded) return;
      try {
        const res = await HttpClient.get("/bank-accounts");
        if (res.data && res.data.success) {
          commit("SET_BANK_ACCOUNTS", res.data.data);
        }
      } catch (e) {
        // silent
      }
    },
    setCouponCode({ commit }, code) {
      commit("SET_COUPON_CODE", code);
    },
    clearCoupon({ commit }) {
      commit("CLEAR_COUPON");
    },

    async fetchPlans({ commit, state }) {
      if (state.plansLoaded) return;
      try {
        const response = await HttpClient.get("/plans");
        if (response.data.success && response.data.data.length > 0) {
          commit("SET_PLANS", response.data.data);
        }
      } catch {
        // Keep empty plans on error
      }
    },

    async fetchUserPlan({ commit, state }) {
      if (state.userPlanLoaded) return;
      try {
        const response = await HttpClient.get("/user/plan");
        if (response.data.success) {
          commit("SET_USER_PLAN", response.data.data);
        }
      } catch {
        // User may not have a plan
      }
    },

    async validateCoupon({ commit, state }) {
      if (!state.couponCode || !state.selectedPlanSlug) return;
      try {
        const response = await HttpClient.post("/coupons/validate", {
          code: state.couponCode,
          plan_slug: state.selectedPlanSlug,
          billing_period: state.billingPeriod
        });
        commit("SET_COUPON_VALID", response.data);
      } catch {
        commit("SET_COUPON_VALID", {
          valid: false,
          error: "Error al validar el cupón"
        });
      }
    },

    // warning: comunicado preventivo (aún hay cupo) en vez de "límite alcanzado".
    // Carga los planes primero: sin ellos el modal no puede recomendar el siguiente.
    async triggerLimitModal(
      { commit, dispatch, getters },
      { feature, usage, warning }
    ) {
      await dispatch("fetchPlans");
      const resolvedUsage = usage || getters.usageByFeature(feature);
      commit("SHOW_LIMIT_MODAL", { feature, usage: resolvedUsage, warning });
    },
    hideLimitModal({ commit }) {
      commit("HIDE_LIMIT_MODAL");
    },

    // Backward compat aliases for old component code
    setPlanTypeSelect({ commit }, type) {
      commit("SET_BILLING_PERIOD", type === "yearly" ? "annual" : "monthly");
    },
    setPlanSlug({ commit }, slug) {
      commit("SET_SELECTED_PLAN", slug);
    }
  },
  getters: {
    // New getters
    plans: s => s.plans,
    activePlans: s => s.plans.filter(p => !p.is_custom),
    customPlan: s => s.plans.find(p => p.is_custom),
    billingPeriod: s => s.billingPeriod,
    selectedPlan: s => s.plans.find(p => p.slug === s.selectedPlanSlug),
    userPlan: s => s.userPlan,
    userPlanLoaded: s => s.userPlanLoaded,
    paymentMethod: s => s.paymentMethod,
    bankAccounts: s => s.bankAccounts,
    couponCode: s => s.couponCode,
    couponValid: s => s.couponValid,
    firma: s => s.firma,
    marketingAddon: s => s.marketingAddon,

    isCurrentPlan: s => slug => {
      return s.userPlan?.plan?.name === slug;
    },

    getPlanPricing: s => (slug, period) => {
      const plan = s.plans.find(p => p.slug === slug);
      const bp = period || s.billingPeriod;
      return plan?.pricing?.[bp] ?? null;
    },

    hasFeature: s => key => {
      // Wait for plan to load — deny access to prevent flash-of-content and early API calls
      if (!s.userPlanLoaded) return false;
      // No active subscription → deny all gated features
      if (!s.userPlan || !s.userPlan.features) return false;
      const feature = s.userPlan.features[key];
      // Feature absent from plan map → treat as disabled (fail-closed)
      return feature ? feature.enabled : false;
    },

    getFeatureValue: s => key => {
      if (!s.userPlan || !s.userPlan.features) return null;
      const feature = s.userPlan.features[key];
      return feature ? feature.value : null;
    },

    documentUsage: s => {
      return (
        s.userPlan?.usage?.documents ?? { used: 0, limit: 0, percentage: 0 }
      );
    },

    canCreateDocument: s => {
      const usage = s.userPlan?.usage?.documents;
      if (!usage || !usage.limit) return true;
      return usage.used < usage.limit;
    },

    signingUsage: s => {
      return (
        s.userPlan?.usage?.document_signing ?? {
          used: 0,
          limit: null,
          percentage: 0
        }
      );
    },

    canSign: s => {
      const usage = s.userPlan?.usage?.document_signing;
      if (!usage || usage.limit === null) return true;
      return usage.used < usage.limit;
    },

    aiUsage: s => {
      return (
        s.userPlan?.usage?.ai_credits ?? {
          used: 0,
          limit: null,
          remaining: null,
          percentage: 0,
          resets_at: null
        }
      );
    },

    hasAiCredits: s => {
      const usage = s.userPlan?.usage?.ai_credits;
      if (!usage || usage.limit === null) return true;
      return usage.remaining === null || usage.remaining > 0;
    },

    limitModal: s => s.limitModal,

    usageByFeature: s => slug => {
      if (slug === "documents_monthly_limit") {
        return (
          s.userPlan?.usage?.documents ?? { used: 0, limit: 0, percentage: 0 }
        );
      }
      if (slug === "ai_credits_monthly") {
        const ai = s.userPlan?.usage?.ai_credits ?? {};
        return {
          used: ai.used ?? 0,
          limit: ai.limit ?? 0,
          percentage: ai.percentage ?? 100
        };
      }
      if (slug === "document_signing_monthly_limit") {
        return (
          s.userPlan?.usage?.document_signing ?? {
            used: 0,
            limit: 0,
            percentage: 0
          }
        );
      }
      if (slug === "bulk_sending") {
        const bulk = s.userPlan?.usage?.bulk_sending ?? {};
        return {
          used: bulk.used ?? 0,
          limit: bulk.limit ?? 0,
          percentage: bulk.percentage ?? 100
        };
      }
      return { used: 0, limit: 0, percentage: 0 };
    },

    isLegacyPlan: s => {
      return s.userPlan?.plan?.is_legacy === true;
    },

    nextUpgradePlan: s => feature => {
      if (!s.plans.length || !s.userPlan) return null;
      const currentSlug = s.userPlan.plan?.name;
      const currentPlanIndex = s.plans.findIndex(p => p.slug === currentSlug);
      const plansAfterCurrent =
        currentPlanIndex >= 0 ? s.plans.slice(currentPlanIndex + 1) : s.plans;
      const nonCustom = plansAfterCurrent.filter(p => !p.is_custom);

      // Find first plan that actually increases the feature limit
      const currentLimit = parseInt(
        s.userPlan?.features?.[feature]?.value ?? "0",
        10
      );
      const next = nonCustom.find(p => {
        const feat = p.features?.find
          ? p.features.find(f => f.slug === feature)
          : null;
        if (!feat) return false;
        if (feat.value === "unlimited") return true;
        return parseInt(feat.value, 10) > currentLimit;
      });
      return next || nonCustom[0] || null;
    },

    // Backward compat getters for old component code
    getPlans: s => s.plans,
    getPlanCurrent: s => s.plans.find(p => p.slug === s.selectedPlanSlug),
    getPlanTypeSelect: s =>
      s.billingPeriod === "annual" ? "yearly" : "monthly",
    getPlanSlugSelect: s => s.selectedPlanSlug,
    getFirma: s => s.firma,
    getMarketingAddon: s => s.marketingAddon,
    plansLoaded: s => s.plansLoaded,
    // eslint-disable-next-line no-unused-vars
    getPlansByType: s => _type => {
      return s.plans;
    }
  }
};
